import { Injectable, Type, inject } from '@angular/core';
import { WizardStepComponent } from '../components/wizard-step/wizard-step.component';
import { StepId, STEP_TITLES } from '../models/wizard.models';
import { WizardConfigurationService } from './wizard-configuration.service';

/**
 * Service for managing dynamic step component registration and loading
 * This service handles the registration of step components and provides
 * methods for dynamic component loading based on step configuration
 */
@Injectable({
  providedIn: 'root',
})
export class WizardStepRegistryService {
  private readonly configService = inject(WizardConfigurationService);
  private componentRegistry = new Map<string, Type<WizardStepComponent>>();
  private loadingPromises = new Map<
    string,
    Promise<Type<WizardStepComponent>>
  >();

  constructor() {
    this.initializeDefaultComponents();
  }

  /**
   * Initialize default component mappings
   * This will be updated as step components are implemented
   */
  private initializeDefaultComponents(): void {
    // Register implemented step components with lazy loading
    this.registerComponent(StepId.SOPRALLUOGO, () =>
      import('../components/steps/sopralluogo-step/sopralluogo-step.component').then(
        (m) => m.SopralluogoStepComponent,
      ),
    );

    this.registerComponent(StepId.PIANIFICA_RIMOZIONE, () =>
      import('../components/steps/pianifica-rimozione-step/pianifica-rimozione-step.component').then(
        (m) => m.PianificaRimozioneStepComponent,
      ),
    );

    this.registerComponent(StepId.SELEZIONA_ATTREZZATURA, () =>
      import('../components/steps/seleziona-attrezzatura-step/seleziona-attrezzatura-step.component').then(
        (m) => m.SelezionaAttrezzaturaStepComponent,
      ),
    );

    this.registerComponent(StepId.AGGIUNGI_ATTREZZATURE, () =>
      import('../components/steps/aggiungi-attrezzature-step/aggiungi-attrezzature-step.component').then(
        (m) => m.AggiungiattrezzatureStepComponent,
      ),
    );

    this.registerComponent(StepId.PIANIFICA_CONSEGNA, () =>
      import('../components/steps/pianifica-consegna-step/pianifica-consegna-step.component').then(
        (m) => m.PianificaConsegnaStepComponent,
      ),
    );

    this.registerComponent(StepId.CONSEGNA, () =>
      import('../components/steps/consegna-step/consegna-step.component').then(
        (m) => m.ConsegnaStepComponent,
      ),
    );

    this.registerComponent(StepId.RIEPILOGO, () =>
      import('../components/steps/riepilogo-step/riepilogo-step.component').then(
        (m) => m.RiepilogoStepComponent,
      ),
    );

    //CRM Step Components
    this.registerComponent(StepId.RICHIEDENTE, () =>
      import('../components/steps/richiedente-step/richiedente-step.component').then(
        (m) => m.RichiedenteStepComponent,
      ),
    );

    this.registerComponent(StepId.INDIRIZZO, () =>
      import('../components/steps/indirizzo-step/indirizzo-step.component').then(
        (m) => m.IndirizzoStepComponent,
      ),
    );

    this.registerComponent(StepId.DETTAGLIO_TICKET, () =>
      import('../components/steps/dettaglio-ticket-step/dettaglio-ticket-step.component').then(
        (m) => m.DettaglioTicketStepComponent,
      ),
    );

    this.registerComponent(StepId.CALENDARIO, () =>
      import('../components/steps/calendario-step/calendario-step.component').then(
        (m) => m.CalendarioStepComponent,
      ),
    );

    this.registerComponent(StepId.ALLEGATI, () =>
      import('../components/steps/allegati-step/allegati-step.component').then(
        (m) => m.AllegatiStepComponent,
      ),
    );

    this.registerComponent(StepId.SERVIZI_DATI_GENERALI, () =>
      import('../components/steps/servizi/dati-generali/dati-generali.component').then(
        (m) => m.DatiGeneraliComponent,
      ),
    );

    this.registerComponent(StepId.SERVIZI_CALENDARIO, () =>
      import('../components/steps/servizi/calendario/calendario.component').then(
        (m) => m.CalendarioComponent,
      ),
    );

    this.registerComponent(StepId.SERVIZI_CONFIGURAZIONE_OPERATIVA, () =>
      import('../components/steps/servizi/configurazione-operativa/configurazione-operativa.component').then(
        (m) => m.ConfigurazioneOperativaComponent,
      ),
    );

    this.registerComponent(StepId.SERVIZI_UTENZE_COINVOLTE, () =>
      import('../components/steps/servizi/utenze-coinvolte/utenze-coinvolte.component').then(
        (m) => m.UtenzeCoinvolteComponent,
      ),
    );
  }

  /**
   * Register a step component with lazy loading support
   * @param stepId - The step ID
   * @param componentLoader - Function that returns a promise resolving to the component class
   */
  registerComponent(
    stepId: string,
    componentLoader: () => Promise<Type<WizardStepComponent>>,
  ): void {
    // Store the loading promise for lazy loading
    this.loadingPromises.set(stepId, componentLoader());
  }

  /**
   * Register a step component directly (for eager loading)
   * @param stepId - The step ID
   * @param component - The component class
   */
  registerComponentDirect(
    stepId: string,
    component: Type<WizardStepComponent>,
  ): void {
    this.componentRegistry.set(stepId, component);

    // Also register in the configuration service
    this.configService.registerStep(stepId, {
      id: stepId,
      title: this.getStepTitle(stepId),
      component,
      isRequired: this.isStepRequired(stepId),
    });
  }

  /**
   * Load a step component dynamically
   * @param stepId - The step ID
   * @returns Promise that resolves to the component class
   */
  async loadComponent(
    stepId: string,
  ): Promise<Type<WizardStepComponent> | null> {
    // Check if component is already loaded
    if (this.componentRegistry.has(stepId)) {
      return this.componentRegistry.get(stepId)!;
    }

    // Check if there's a loading promise
    if (this.loadingPromises.has(stepId)) {
      try {
        const component = await this.loadingPromises.get(stepId)!;

        // Store the loaded component for future use
        this.componentRegistry.set(stepId, component);

        // Register in configuration service
        this.configService.registerStep(stepId, {
          id: stepId,
          title: this.getStepTitle(stepId),
          component,
          isRequired: this.isStepRequired(stepId),
        });

        // Remove the loading promise since it's now loaded
        this.loadingPromises.delete(stepId);

        return component;
      } catch (error) {
        this.loadingPromises.delete(stepId);
        return null;
      }
    }
    return null;
  }

  /**
   * Load multiple step components
   * @param stepIds - Array of step IDs to load
   * @returns Promise that resolves to a map of stepId -> component
   */
  async loadComponents(
    stepIds: string[],
  ): Promise<Map<string, Type<WizardStepComponent>>> {
    const componentMap = new Map<string, Type<WizardStepComponent>>();

    const loadPromises = stepIds.map(async (stepId) => {
      const component = await this.loadComponent(stepId);
      if (component) {
        componentMap.set(stepId, component);
      }
    });

    await Promise.all(loadPromises);
    return componentMap;
  }

  /**
   * Check if a component is loaded for a step
   * @param stepId - The step ID
   * @returns True if component is loaded
   */
  isComponentLoaded(stepId: string): boolean {
    return this.componentRegistry.has(stepId);
  }

  /**
   * Get all loaded components
   * @returns Map of stepId -> component
   */
  getLoadedComponents(): Map<string, Type<WizardStepComponent>> {
    return new Map(this.componentRegistry);
  }

  /**
   * Preload components for an activity type
   * @param activityTypeId - The activity type ID
   * @returns Promise that resolves when all components are loaded
   */
  async preloadComponentsForActivityType(
    activityTypeId: string,
  ): Promise<void> {
    const stepIds = this.configService.getStepsForActivityType(activityTypeId);
    await this.loadComponents(stepIds);
  }

  /**
   * Clear component cache (useful for testing or memory management)
   */
  clearComponentCache(): void {
    this.componentRegistry.clear();
    this.loadingPromises.clear();
  }

  /**
   * Get component loading status for all steps
   * @returns Object with loading status for each step
   */
  getComponentLoadingStatus(): Record<
    string,
    'loaded' | 'loading' | 'not_registered'
  > {
    const allStepIds = this.configService.getAllAvailableStepIds();
    const status: Record<string, 'loaded' | 'loading' | 'not_registered'> = {};

    allStepIds.forEach((stepId) => {
      if (this.componentRegistry.has(stepId)) {
        status[stepId] = 'loaded';
      } else if (this.loadingPromises.has(stepId)) {
        status[stepId] = 'loading';
      } else {
        status[stepId] = 'not_registered';
      }
    });

    return status;
  }

  /**
   * Register all step components with lazy loading
   * This method sets up lazy loading for all step components
   * For now, it registers placeholder components since step components are not yet implemented
   */
  registerAllStepComponents(): void {
    // Register placeholder components for steps that aren't implemented yet
    // Don't override already registered components
    const stepIds: string[] = [
      // All step components are now implemented
    ];

    stepIds.forEach((stepId) => {
      // Only register placeholder if not already registered
      if (
        !this.loadingPromises.has(stepId) &&
        !this.componentRegistry.has(stepId)
      ) {
        this.registerComponent(stepId, () =>
          Promise.resolve(this.createPlaceholderComponent(stepId)),
        );
      }
    });
  }

  /**
   * Create a placeholder component for steps that aren't implemented yet
   * @param stepId - The step ID
   * @returns A placeholder component class
   */
  private createPlaceholderComponent(
    stepId: string,
  ): Type<WizardStepComponent> {
    // Create a dynamic placeholder component class
    const PlaceholderComponent = class extends WizardStepComponent {
      override stepId: any = stepId;

      get stepData(): any {
        return null;
      }

      get isValid(): boolean {
        return true; // Placeholder is always valid
      }

      validate(): boolean {
        return true;
      }

      saveStepData(): void {}

      loadStepData(): void {}

      resetStep(): void {}
    };

    // Set a display name for debugging
    Object.defineProperty(PlaceholderComponent, 'name', {
      value: `PlaceholderComponent_${stepId}`,
    });

    return PlaceholderComponent as Type<WizardStepComponent>;
  }

  /**
   * Get step title from configuration
   * @param stepId - The step ID
   * @returns The step title
   */
  private getStepTitle(stepId: string): string {
    return STEP_TITLES[stepId] || stepId;
  }

  /**
   * Check if a step is required for any activity type
   * @param stepId - The step ID
   * @returns True if the step is required
   */
  private isStepRequired(stepId: string): boolean {
    const activityTypes = this.configService.getActivityTypesForStep(stepId);
    return activityTypes.some((activityTypeId) => {
      const requiredSteps =
        this.configService.getRequiredStepsForActivityType(activityTypeId);
      return requiredSteps.includes(stepId);
    });
  }

  /**
   * Validate that all required components are registered
   * @returns Validation result
   */
  validateComponentRegistration(): {
    isValid: boolean;
    missingComponents: string[];
    loadingComponents: string[];
  } {
    const allStepIds = this.configService.getAllAvailableStepIds();
    const missingComponents: string[] = [];
    const loadingComponents: string[] = [];

    allStepIds.forEach((stepId) => {
      if (
        !this.componentRegistry.has(stepId) &&
        !this.loadingPromises.has(stepId)
      ) {
        missingComponents.push(stepId);
      } else if (this.loadingPromises.has(stepId)) {
        loadingComponents.push(stepId);
      }
    });

    return {
      isValid: missingComponents.length === 0,
      missingComponents,
      loadingComponents,
    };
  }

  /**
   * Get component registration statistics
   * @returns Statistics about component registration
   */
  getRegistrationStatistics(): {
    totalSteps: number;
    loadedComponents: number;
    loadingComponents: number;
    missingComponents: number;
    registrationPercentage: number;
  } {
    const allStepIds = this.configService.getAllAvailableStepIds();
    const totalSteps = allStepIds.length;
    const loadedComponents = this.componentRegistry.size;
    const loadingComponents = this.loadingPromises.size;
    const missingComponents = totalSteps - loadedComponents - loadingComponents;
    const registrationPercentage = Math.round(
      (loadedComponents / totalSteps) * 100,
    );

    return {
      totalSteps,
      loadedComponents,
      loadingComponents,
      missingComponents,
      registrationPercentage,
    };
  }
}
